import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import {
  Environment,
  FINISHES,
  Holder,
  Puck,
  useHolderGeometries,
  usePuckGeometry,
  useSheetGeometry,
} from "@/components/three/shared";

const ORANGE = new THREE.Color("#FF6B00");
const BLACK = new THREE.Color("#000000");

// y positions: [assembled, exploded]
const LAYOUT = {
  tag: [0.6, 2.7],
  holder: [0, 0.9],
  magnet: [-0.08, -0.95],
  sheet: [-0.28, -2.35],
  sheet2: [-0.36, -2.95],
};

/** Animates a part's y position + highlight glow. */
const Part = ({ id, explode, highlight, children }) => {
  const group = useRef();
  const [a, b] = LAYOUT[id];
  useFrame((_, dt) => {
    const g = group.current;
    if (!g) return;
    const targetY = a + (b - a) * explode.current;
    g.position.y = THREE.MathUtils.damp(g.position.y, targetY, 5, dt);
    const s = THREE.MathUtils.damp(g.scale.x, highlight === id ? 1.05 : 1, 8, dt);
    g.scale.setScalar(s);
    g.traverse((o) => {
      if (o.isMesh && o.material?.emissive && !o.userData.led) {
        o.material.emissive.lerp(highlight === id ? ORANGE : BLACK, 1 - Math.exp(-8 * dt));
        o.material.emissiveIntensity = highlight === id ? 0.22 : 1;
      }
    });
  });
  return (
    <group ref={group} position={[0, a, 0]}>
      {children}
    </group>
  );
};

const TagModel = ({ finish = "obsidian", explode, highlight, spin }) => {
  const outer = useRef();
  const inner = useRef();
  const colors = FINISHES[finish];
  const puckGeo = usePuckGeometry();
  const holderGeos = useHolderGeometries();
  const sheetGeo = useSheetGeometry();

  useFrame((state, dt) => {
    if (!outer.current || !inner.current) return;
    const base = spin.current.dragging ? 0 : 0.42 * dt;
    outer.current.rotation.y += base + spin.current.vel;
    spin.current.vel *= 0.94;
    inner.current.position.y = Math.sin(state.clock.elapsedTime * 0.9) * 0.08;
  });

  return (
    <>
      <Environment />
      <ambientLight intensity={0.55} />
      <directionalLight
        position={[5, 9, 6]}
        intensity={2.4}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0004}
      />
      <directionalLight position={[-7, 3, -5]} intensity={0.9} />
      <spotLight position={[0, 6, -9]} intensity={14} angle={0.6} penumbra={1} color="#ffd9bd" />

      <group rotation={[0.42, 0, 0]} position={[0, -0.2, 0]}>
        <group ref={outer}>
          <group ref={inner}>
            <Part id="tag" explode={explode} highlight={highlight}>
              <Puck colors={colors} geometry={puckGeo} />
            </Part>

            <Part id="holder" explode={explode} highlight={highlight}>
              <Holder colors={colors} geometries={holderGeos} />
            </Part>

            <Part id="magnet" explode={explode} highlight={highlight}>
              <mesh castShadow receiveShadow>
                <cylinderGeometry args={[0.5, 0.5, 0.18, 64]} />
                <meshStandardMaterial color="#7a7d84" metalness={0.95} roughness={0.28} />
              </mesh>
            </Part>

            <Part id="sheet" explode={explode} highlight={highlight}>
              <mesh geometry={sheetGeo} castShadow receiveShadow>
                <meshStandardMaterial color="#d4d7dc" metalness={1} roughness={0.32} />
              </mesh>
            </Part>
            <Part id="sheet2" explode={explode} highlight={highlight === "sheet" ? "sheet2" : highlight}>
              <mesh geometry={sheetGeo} castShadow receiveShadow>
                <meshStandardMaterial color="#cfd2d7" metalness={1} roughness={0.34} />
              </mesh>
            </Part>
          </group>
        </group>

        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -3.9, 0]} receiveShadow>
          <planeGeometry args={[30, 30]} />
          <shadowMaterial opacity={0.035} />
        </mesh>
      </group>
    </>
  );
};

export default TagModel;
